

import { useState, useEffect } from "react";
import { fetchContactPage } from "../../services/api";
import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import { useLocation } from "react-router-dom";

const Base_URL = process.env.REACT_APP_Base_URL + "/api/contact-forms";
const STRAPI_URL = process.env.REACT_APP_Base_URL;

const emptyForm = {
  Full_Name: "",
  Company: "",
  Email: "",
  Message: "",
};

const t = {
  en: {
    section: "Contact Us",
    name: "*Name",
    company: "Company",
    email: "*Email",
    message: "*Message",
    submit: "Submit",
    success: "Message sent! We'll get back to you soon.",
    error: "Something went wrong. Please try again.",
    nameError: "Name is required",
    emailError: "Email is required",
    emailInvalid: "Enter a valid email",
    messageError: "Message is required",
  },
  ja: {
    section: "お問い合わせ",
    name: "*お名前",
    company: "会社名",
    email: "*メールアドレス",
    message: "*メッセージ",
    submit: "送信",
    success: "送信しました。追ってご連絡いたします。",
    error: "エラーが発生しました。もう一度お試しください。",
    nameError: "名前は必須です",
    emailError: "メールは必須です",
    emailInvalid: "有効なメールを入力してください",
    messageError: "メッセージは必須です",
  },
};

export default function ContactForm({ lang: propLang }) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const [pageData, setPageData] = useState(null);

  const location = useLocation();

  // ✅ FIX: URL overrides prop language
  const lang = location.pathname.startsWith("/ja")
    ? "ja"
    : propLang || "en";

  const text = t[lang];

 // After fetching pageData, add a fallback fetch for image
const [fallbackImageUrl, setFallbackImageUrl] = useState(null);

useEffect(() => {
  const loadData = async () => {
    const data = await fetchContactPage(lang);
    if (data) setPageData(data);

    // If Japanese and no image, fetch English as fallback
    if (lang === "ja" && !data?.Contact_Form_Image?.url) {
      const enData = await fetchContactPage("en");
      if (enData?.Contact_Form_Image?.url) {
        setFallbackImageUrl(`${STRAPI_URL}${enData.Contact_Form_Image.url}`);
      }
    }
  };
  loadData();
}, [lang]);
useEffect(() => {
  setErrors({});
  setStatus(null);
  setStatusMsg("");
}, [lang]);
  const validate = () => {
    const e = {};
    if (!form.Full_Name.trim()) e.Full_Name = text.nameError;
    if (!form.Email.trim()) e.Email = text.emailError;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.Email))
      e.Email = text.emailInvalid;
    if (!form.Message.trim()) e.Message = text.messageError;
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const isLoading = status === "loading";

  const handleSubmit = async () => {
    if (isLoading) return;
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    setStatus("loading");

    try {
      const res = await fetch(Base_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: form }),
      });

      if (res.ok) {
        setStatus("success");
        setStatusMsg(text.success);
        setForm(emptyForm);
      } else {
        throw new Error();
      }
    } catch {
      setStatus("error");
      setStatusMsg(text.error);
    }
  };

const imageUrl = pageData?.Contact_Form_Image?.url
  ? `${STRAPI_URL}${pageData.Contact_Form_Image.url}`
  : fallbackImageUrl || null;  // ← falls back to English image

  return (
    <>
      <div className="padding-x-40 padding-y-80 custom-lg-flex">

        {/* LEFT */}
        <div className="contact-wrapper w-[100%]">

          <div>
            <div className="section-name-wrapper">
              <div className="section-name-dot"></div>
              <h6 className="section-name">{text.section}</h6>
            </div>
          </div>

          <div className="cf-wrapper">
            <div className="cf-container">

              <div className="cf-row">
                <div className="cf-field">
                  <input
                    className={`cf-input${errors.Full_Name ? " error" : ""}`}
                    name="Full_Name"
                    value={form.Full_Name}
                    onChange={handleChange}
                    placeholder=" "
                    disabled={isLoading}
                  />
                  <label className="cf-label">{text.name}</label>
                  {errors.Full_Name && (
                  <span className="cf-error-text">{errors.Full_Name}</span>
                  )}
                </div>

                <div className="cf-field">
                  <input
                    className="cf-input"
                    name="Company"
                    value={form.Company}
                    onChange={handleChange}
                    placeholder=" "
                    disabled={isLoading}
                  />
                  <label className="cf-label">{text.company}</label>
                </div>
              </div>

              <div className="cf-field full">
                <input
                  className={`cf-input${errors.Email ? " error" : ""}`}
                  name="Email"
                  type="email"
                  value={form.Email}
                  onChange={handleChange}
                  placeholder=" "
                  disabled={isLoading}
                />
                <label className="cf-label">{text.email}</label>
                {errors.Email && (
                <span className="cf-error-text">{errors.Email}</span>
                )}
              </div>

              <div className="cf-field">
                <textarea
                  className={`cf-textarea${errors.Message ? " error" : ""}`}
                  name="Message"
                  value={form.Message}
                  onChange={handleChange}
                  placeholder=" "
                  disabled={isLoading}
                />
                <label className="cf-label">{text.message}</label>
                {errors.Message && (
                <span className="cf-error-text">{errors.Message}</span>
                )}
              </div>

              {/* STRAPI RICH TEXT */}
              <div className="text-dark-blue-color font-medium">
                {pageData?.Acknowledgment && Array.isArray(pageData.Acknowledgment) && (
                  <div className="cf-terms pt-[20px]">
                    <BlocksRenderer content={pageData.Acknowledgment}
                      blocks={{
    link: ({ children, url }) => (
      <a href={url} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  }} />
                  </div>
                )}
              </div>

              <button
                className="size-h3 text-accent-blue underline align-center flex"
                style={{ marginBottom: 0 }}
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {text.submit}
                {isLoading && <span className="cf-loading ps-2" />}
              </button>

              {status && status !== "loading" && (
                <div className={`cf-status ${status}`}>{statusMsg}</div>
              )}

            </div>
          </div>
        </div>

        {/* RIGHT */}
        {/* <div className="col-lg-6 overflow-hidden rounded-[5px] pt-[40px] md:pt-0 hidden md:block">
          {imageUrl && (
            <img
              className="w-full rounded-[5px] hover:scale-110 transition-transform duration-300"
              src={imageUrl}
              alt="Contact"
            />
          )}
        </div> */}

      </div>
    </>
  );
}